// Colunas de tabela redimensionáveis pelo usuário, com largura persistida.
//
// Feito à mão de propósito: a receita oficial do antd usa `react-resizable`,
// que não está no projeto. São ~50 linhas de mousemove — não vale uma
// dependência nova (e mais um pacote no bundle) por isso.
//
// Uso:
//   const { colunas, componentes, restaurar, ajustado } =
//     useColunasRedimensionaveis('op:contratos:larguras', colunasBase);
//   <Table columns={colunas} components={componentes} tableLayout="fixed" />
//
// A largura é gravada no localStorage por CHAVE, então duas tabelas que
// compartilham a mesma chave (ex.: contratos formais e livres) redimensionam
// juntas — que é o comportamento esperado quando são a mesma lista partida em
// duas seções.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';

const LARGURA_MIN = 50;
const LARGURA_MAX = 900;

type Larguras = Record<string, number>;

function ler(chave: string): Larguras {
  try {
    const raw = localStorage.getItem(chave);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    // Sanitiza: ignora entradas fora da faixa (storage editado à mão ou de
    // uma versão antiga da tela com colunas diferentes).
    const out: Larguras = {};
    for (const [k, v] of Object.entries(obj)) {
      const n = Number(v);
      if (Number.isFinite(n) && n >= LARGURA_MIN && n <= LARGURA_MAX) out[k] = n;
    }
    return out;
  } catch { return {}; }
}

// Célula de cabeçalho com a alça de arrasto na borda direita.
//
// ⚠ A alça PRECISA ser visível. A primeira versão era uma faixa transparente
//   de 9px: funcionava perfeitamente nos testes e ninguém conseguia usar,
//   porque não havia como adivinhar que ela existia. Affordance aqui não é
//   enfeite — é o que faz o recurso existir para quem usa.
function CelulaCabecalho({ onResize, width, ...props }: any) {
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const arrastando = useRef<{ x0: number; w0: number; cols: HTMLElement[]; atual: number } | null>(null);
  const [sobre, setSobre] = useState(false);
  const [ativo, setAtivo] = useState(false);

  // Handler guardado em ref para o efeito abaixo poder montar UMA vez só.
  // Sem isso, `onResize` muda a cada render e o efeito remove/readiciona os
  // listeners de window a CADA pixel arrastado.
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  useEffect(() => {
    // ⚠ PERFORMANCE: durante o arrasto mexemos direto no <col> do DOM, sem
    //   passar pelo React. A primeira versão chamava setState a cada
    //   mousemove, o que re-renderizava a tabela INTEIRA (centenas de linhas)
    //   dezenas de vezes por segundo — o arrasto ficava travado. O React só
    //   entra no fim, com um único render que fixa o valor definitivo.
    function mover(e: MouseEvent) {
      const a = arrastando.current;
      if (!a) return;
      const nova = Math.min(LARGURA_MAX, Math.max(LARGURA_MIN, a.w0 + (e.clientX - a.x0)));
      a.atual = nova;
      for (const col of a.cols) col.style.width = `${nova}px`;
    }
    function soltar() {
      const a = arrastando.current;
      if (!a) return;
      arrastando.current = null;
      setAtivo(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      onResizeRef.current?.(a.atual, true);   // 1 render, no fim
    }
    window.addEventListener('mousemove', mover);
    window.addEventListener('mouseup', soltar);
    return () => {
      window.removeEventListener('mousemove', mover);
      window.removeEventListener('mouseup', soltar);
    };
  }, []);   // monta uma vez — os dados do arrasto vivem em refs

  if (!onResize) return <th {...props} />;

  // Acha os <col> desta coluna. Com scroll o antd renderiza DUAS tabelas
  // (uma do cabeçalho, outra do corpo) dentro do mesmo .ant-table — as duas
  // precisam acompanhar, senão cabeçalho e linhas desalinham durante o arrasto.
  function colsDaColuna(): HTMLElement[] {
    const th = thRef.current;
    if (!th || !th.parentElement) return [];
    const idx = Array.prototype.indexOf.call(th.parentElement.children, th);
    const raiz = th.closest('.ant-table') || th.closest('table')?.parentElement;
    if (!raiz) return [];
    const achados: HTMLElement[] = [];
    raiz.querySelectorAll('colgroup').forEach((cg) => {
      const c = cg.children[idx] as HTMLElement | undefined;
      if (c) achados.push(c);
    });
    return achados;
  }

  const destacado = sobre || ativo;

  return (
    <th ref={thRef} {...props} style={{ ...props.style, position: 'relative' }}>
      {props.children}
      <div
        onMouseDown={(e) => {
          // stopPropagation é obrigatório: o <th> do antd tem onClick de
          // ordenação, e sem isso todo arrasto também reordenaria a tabela.
          e.preventDefault();
          e.stopPropagation();
          const w0 = Number(width) || thRef.current?.getBoundingClientRect().width || 100;
          arrastando.current = { x0: e.clientX, w0, cols: colsDaColuna(), atual: w0 };
          setAtivo(true);
          document.body.style.cursor = 'col-resize';
          document.body.style.userSelect = 'none';
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setSobre(true)}
        onMouseLeave={() => setSobre(false)}
        title="Arraste para ajustar a largura desta coluna"
        style={{
          // ⚠ right: 0 e NÃO um valor negativo. Com vazamento pra fora do <th>,
          //   a alça da ÚLTIMA coluna cai fora da área visível da tabela e é
          //   cortada pelo overflow — ela simplesmente não podia ser agarrada
          //   (13/08/2026). Aqui a área fica inteira dentro da célula e cresce
          //   pra ESQUERDA, com a barrinha encostada na divisa.
          position: 'absolute', right: 0, top: 0, bottom: 0, width: 12,
          cursor: 'col-resize', zIndex: 3, touchAction: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        }}
      >
        <span
          style={{
            display: 'block',
            width: destacado ? 3 : 1,
            height: destacado ? '100%' : '55%',
            borderRadius: 2,
            // Sempre visível em cinza: é o que avisa "dá pra puxar aqui".
            // Azul da marca enquanto arrasta ou passa o mouse.
            background: destacado ? '#0930D0' : '#d9d9d9',
            transition: 'background .12s, width .12s, height .12s',
          }}
        />
      </div>
    </th>
  );
}

// Só o controle ("já mexeram?" + "voltar ao padrão"), para quem quer o botão
// de restaurar num componente diferente daquele que desenha a tabela — caso
// típico: a barra de filtros no pai, a tabela no filho. Compartilha a mesma
// chave e o mesmo evento, então os dois ficam em sincronia.
export function useControleLarguras(chave: string) {
  const [ajustado, setAjustado] = useState(() => Object.keys(ler(chave)).length > 0);

  useEffect(() => {
    const evento = `larguras-colunas:${chave}`;
    const sincronizar = () => setAjustado(Object.keys(ler(chave)).length > 0);
    window.addEventListener(evento, sincronizar);
    return () => window.removeEventListener(evento, sincronizar);
  }, [chave]);

  const restaurar = useCallback(() => {
    try { localStorage.removeItem(chave); } catch { /* ignora */ }
    window.dispatchEvent(new CustomEvent(`larguras-colunas:${chave}`));
  }, [chave]);

  return { ajustado, restaurar };
}

// Devolve `aplicar(colunas)` em vez de receber as colunas prontas: em telas
// reais a definição das colunas costuma vir DEPOIS de um early return (ex.:
// "sem linhas → não renderiza"), e um hook não pode ficar abaixo disso. Assim o
// hook fica no topo do componente e a aplicação acontece onde as colunas
// existem.
export function useColunasRedimensionaveis<T>(chave: string) {
  const [larguras, setLarguras] = useState<Larguras>(() => ler(chave));
  // Durante o arrasto o state muda a cada pixel; só gravamos no storage ao
  // soltar (senão é uma escrita síncrona por frame).
  const pendente = useRef<Larguras>(larguras);

  // Duas tabelas com a mesma chave (contratos formais e livres) têm instâncias
  // separadas do hook. Sem este aviso, redimensionar uma deixaria a outra com a
  // largura antiga até a página recarregar — e o usuário veria duas tabelas da
  // MESMA lista desalinhadas. Só ao soltar, pra não re-renderizar por frame.
  const EVENTO = `larguras-colunas:${chave}`;
  useEffect(() => {
    function sincronizar() {
      const lidas = ler(chave);
      pendente.current = lidas;
      setLarguras(lidas);
    }
    window.addEventListener(EVENTO, sincronizar);
    return () => window.removeEventListener(EVENTO, sincronizar);
  }, [EVENTO, chave]);

  // Chamado UMA vez por arrasto, ao soltar (durante o movimento quem atualiza
  // é o DOM direto, na célula de cabeçalho). Aceita também chamadas com
  // fim=false — o comportamento continua correto, só re-renderiza mais.
  const aoRedimensionar = useCallback((key: string) => (largura: number | null, fim: boolean) => {
    if (largura != null) {
      pendente.current = { ...pendente.current, [key]: largura };
      setLarguras(pendente.current);
    }
    if (fim) {
      try { localStorage.setItem(chave, JSON.stringify(pendente.current)); } catch { /* cota cheia */ }
      window.dispatchEvent(new CustomEvent(`larguras-colunas:${chave}`));
    }
  }, [chave]);

  const restaurar = useCallback(() => {
    try { localStorage.removeItem(chave); } catch { /* ignora */ }
    pendente.current = {};
    setLarguras({});
    window.dispatchEvent(new CustomEvent(`larguras-colunas:${chave}`));
  }, [chave]);

  const aplicar = useCallback((base: ColumnsType<T>): ColumnsType<T> =>
    base.map((c: any) => {
      const key = String(c.key ?? c.dataIndex ?? '');
      // Coluna sem key não tem como ser persistida — passa reta, sem alça.
      if (!key) return c;
      const width = larguras[key] ?? c.width;
      // MESCLA com o onHeaderCell que a coluna já tinha, em vez de trocar.
      // Substituir descartaria o que a coluna definia ali (onClick, style,
      // aria-*) — um teste pegou isso: a coluna perdia o handler de ordenação
      // e ninguém percebia, porque na tela de contratos o sort vem por outro
      // caminho (`sorter: true` + onChange do Table).
      const originalHeaderCell = c.onHeaderCell;
      return {
        ...c, width,
        onHeaderCell: (col: any, index?: number) => ({
          ...(originalHeaderCell ? originalHeaderCell(col, index) : {}),
          width: col.width,
          onResize: aoRedimensionar(key),
        }),
      };
    }), [larguras, aoRedimensionar]);

  const componentes = useMemo(() => ({ header: { cell: CelulaCabecalho } }), []);

  return { aplicar, componentes, restaurar, ajustado: Object.keys(larguras).length > 0 };
}
